import { HOLD_UNTIL_REPLY_DELETE_REPLY_MODAL } from 'modules/workflows/constants/modals';
import { closeModal, openModal } from 'modules/workflows/ducks/actions';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Delete24, Exit16 } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import {
  Pill,
  RemoveContainer,
  ReplyHeaderContainer,
  ReplyHeaderText,
  ReplyHeaderTextContainer,
} from './HoldUntilReply.styles';
import { Variant } from './HoldUntilReply.types';

//HEADER
export const REPLY_HEADER_TEST_ID = 'reply-header';

const MAX_PILL_SIZE = 300;

type ReplyHeaderProps = {
  replySectionIndex: number;
  showPills: boolean;
  onRemove: (index: number) => void;
  showDeleteButton: boolean;
  onDelete?: () => void;
  isReadOnly?: boolean;
};

export const ReplyHeader = ({
  isReadOnly = false,
  replySectionIndex,
  showPills,
  onRemove,
  showDeleteButton,
  onDelete,
}: ReplyHeaderProps) => {
  const dispatch = useDispatch();
  const listOfVariants = useWatch({ name: `replies[${replySectionIndex}].variants` });

  const pills = useMemo(() => {
    const pills = [] as JSX.Element[];
    let currentPillSize = 0;
    let catchAllPillCount = 0;
    listOfVariants
      ?.filter((x: Variant) => x.variant !== '')
      ?.forEach((reply: Variant, i: number) => {
        const pillSize = reply?.variant?.length * (6 * listOfVariants.length);
        if (catchAllPillCount > 0 || currentPillSize + pillSize > MAX_PILL_SIZE) {
          catchAllPillCount++;
        } else {
          currentPillSize += pillSize;
          pills.push(
            <Pill data-test={`pill-${i}`} key={`pill-${reply.variant}-${i}`}>
              <span>{reply.variant}</span>
              {!isReadOnly && (
                <RemoveContainer
                  data-test={`remove-variant-${i}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(i);
                  }}
                >
                  <Exit16 />
                </RemoveContainer>
              )}
            </Pill>,
          );
        }
      });
    if (catchAllPillCount > 0) {
      pills.push(
        <Pill
          data-test={`catch-all-${catchAllPillCount}`}
          key={`catch-all-${catchAllPillCount}`}
        >{`+${catchAllPillCount}`}</Pill>,
      );
    }

    return pills;
  }, [listOfVariants, onRemove, isReadOnly]);

  const incrementedIndex = replySectionIndex + 1;

  const handleDelete = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation();
    if (listOfVariants.every((x: Variant) => x.variant === '')) {
      onDelete?.();
    } else {
      dispatch(
        openModal(HOLD_UNTIL_REPLY_DELETE_REPLY_MODAL, {
          handleDeleteReply: () => {
            onDelete?.();
            dispatch(closeModal(HOLD_UNTIL_REPLY_DELETE_REPLY_MODAL));
          },
          onClose: () => {
            dispatch(closeModal(HOLD_UNTIL_REPLY_DELETE_REPLY_MODAL));
          },
        }),
      );
    }
  };
  return (
    <ReplyHeaderContainer data-test={`${REPLY_HEADER_TEST_ID}-${incrementedIndex}`}>
      <ReplyHeaderTextContainer>
        <ReplyHeaderText>{`Reply ${incrementedIndex}`}</ReplyHeaderText>
        {showPills && pills}
      </ReplyHeaderTextContainer>
      {showDeleteButton && (
        <Tooltip tooltipContent="Delete reply">
          <IconButton variant={BUTTON_VARIANTS.GHOST} onClick={handleDelete}>
            <Delete24 />
          </IconButton>
        </Tooltip>
      )}
    </ReplyHeaderContainer>
  );
};
